<script lang="ts">
  import { flip } from 'svelte/animate'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { screen } from '#app/lib/components/stores/screen'
  import { LogError } from '#app/lib/loggers'
  import { onScrollToBottom } from '#app/lib/screen'
  import { onChange } from '#app/lib/svelte/svelte'
  import { setIntersection } from '#app/lib/utils'
  import type { EntitiesSortFunction, Facets, FacetsSelectedValues, TextFilterUris } from '#app/types/entity'
  import EntityListCompact from '#entities/components/layouts/entity_list_compact.svelte'
  import EntityListCompactTitleRow from '#entities/components/layouts/entity_list_compact_title_row.svelte'
  import EntityListRow from '#entities/components/layouts/entity_list_row.svelte'
  import MissingEntityButton from '#entities/components/layouts/missing_entity_button.svelte'
  import SectionLabel from '#entities/components/layouts/section_label.svelte'
  import SortEntitiesBy from '#entities/components/layouts/sort_entities_by.svelte'
  import SubEntityGridCard from '#entities/components/layouts/sub_entity_grid_card.svelte'
  import WorkActions from '#entities/components/layouts/work_actions.svelte'
  import { bySearchMatchScore, getSelectedUris } from '#entities/components/lib/sub_entities_browser_helpers'
  import type { SerializedEntity } from '#entities/lib/entities'
  import type { EntityUri } from '#server/types/entity'
  import { i18n } from '#user/lib/i18n'
  import type { SubEntitiesBrowserDisplayMode } from './sub_entities_browser.svelte'
  import type { SubEntitiesBrowserSectionData } from '../lib/entities'

  const {
    section,
    displayMode,
    facets,
    facetsSelectedValues,
    textFilterUris,
  }: {
    section: SubEntitiesBrowserSectionData
    displayMode: SubEntitiesBrowserDisplayMode
    facets: Facets
    facetsSelectedValues: FacetsSelectedValues
    textFilterUris: TextFilterUris
  } = $props()

  const entities = $derived(section.entities)
  const searchable = $derived(section.searchable || true)
  const sortingType = $derived(section.sortingType)
  const isCompactDisplay = $derived(section.isCompactDisplay)
  const label = $derived(section.label)
  const context = $derived(section.context)

  let entitiesSortFunction: EntitiesSortFunction = $state()
  let flash: FlashState = $state()

  $effect(() => {
    onChange(context, () => {
      if (context) {
        flash = {
          type: 'warning',
          message: context,
        }
      }
    })
  })

  const filteredEntities = $derived.by(() => {
    // facetsSelectedValues won't be initialized if SubEntitiesBrowserFacets isn't shown
    if (!facetsSelectedValues) return entities
    if (textFilterUris && !searchable) {
      return []
    }
    let selectedUris = getSelectedUris({ entities, facets, facetsSelectedValues })
    if (textFilterUris) selectedUris = setIntersection(selectedUris, textFilterUris)
    let _filteredEntities = entities
    if (selectedUris.size > 0) {
      _filteredEntities = _filteredEntities.filter(filterSelectedEntities(selectedUris))
    } else {
      _filteredEntities = []
    }
    if (textFilterUris) {
      _filteredEntities = _filteredEntities.sort(bySearchMatchScore(textFilterUris))
    }
    return _filteredEntities
  })

  const filteredEntitiesLength = $derived(filteredEntities?.length || 0)

  let sortedFilteredEntities: SerializedEntity[] = $state([])
  $effect(() => {
    if (!entitiesSortFunction || !filteredEntities) return
    entitiesSortFunction(filteredEntities)
      .then((sortedEntities: SerializedEntity[]) => {
        sortedFilteredEntities = sortedEntities
      })
      .catch(LogError)
  })

  const filterSelectedEntities = (selectedUris: Set<EntityUri>) => (entity: SerializedEntity) => {
    const { uri } = entity
    return selectedUris.has(uri) || isSelectedEntityAParent(uri)
  }

  function isSelectedEntityAParent (uri: EntityUri) {
    // ie. if a collection is selected, display the collection in question.
    // TODO: generalize pattern to series
    return facetsSelectedValues['wdt:P195'] === uri
  }

  const disabledSearch = $derived(textFilterUris && !searchable)

  const entitiesPerRow = 8
  // Limit needs to be high enough to have enough elements in order to be scrollable
  // otherwise on:scroll wont be triggered
  const initialLimit = entitiesPerRow * 4
  let displayLimit: number = $state(initialLimit)
  let scrollableElement: HTMLElement = $state()

  const paginatedEntities = $derived.by(() => {
    return sortedFilteredEntities.slice(0, displayLimit)
  })

  function displayMore () {
    if (displayLimit < filteredEntities.length) {
      const incrementedDisplayLimit = displayLimit + (2 * entitiesPerRow)
      displayLimit = Math.min(incrementedDisplayLimit, filteredEntities.length)
    }
  }

  const anyEntity = $derived(paginatedEntities.length > 0)

  $effect(() => {
    onChange(filteredEntities, resetEntities)
  })
  function resetEntities () {
    if (scrollableElement) scrollableElement.scroll({ top: 0, behavior: 'smooth' })
    displayLimit = initialLimit
  }
</script>

<div
  class="sub-entities-browser-section"
  class:section-without-entity={!anyEntity}
  class:disabled={disabledSearch}
  title={disabledSearch ? i18n('Searching is not possible for this section yet') : ''}
>
  <div
    class="title-row"
    class:empty={!label}
  >
    {#if label}
      <SectionLabel
        {label}
        entitiesLength={entities.length}
        {filteredEntitiesLength}
        alignedWidth={true}
      />
    {/if}
    <SortEntitiesBy
      {sortingType}
      bind:entitiesSortFunction
      entitiesCount={filteredEntitiesLength}
    />
  </div>
  <Flash bind:state={flash} />
  {#if anyEntity}
    <ul
      class:grid={displayMode === 'grid'}
      class:list={displayMode === 'list'}
      onscroll={onScrollToBottom(displayMore)}
      bind:this={scrollableElement}
    >
      {#if isCompactDisplay}
        <EntityListCompactTitleRow />
      {/if}
      {#each paginatedEntities as entity (entity.uri)}
        <li
          animate:flip={{ duration: 300 }}
          class:isCompactDisplay
        >
          {#if isCompactDisplay}
            <!-- known case: article -->
            <EntityListCompact
              {entity}
              relatedEntities={entity.relatedEntities}
            />
          {:else if displayMode === 'grid'}
            <SubEntityGridCard {entity} />
          {:else}
            <EntityListRow
              {entity}
              relatedEntities={entity.relatedEntities}
              listDisplay={true}
            >
              {#snippet actions()}
                <WorkActions
                  {entity}
                  align={$screen.isSmallerThan('$smaller-screen') ? 'center' : 'right'}
                />
              {/snippet}
            </EntityListRow>
          {/if}
        </li>
      {/each}
    </ul>
  {:else}
    <p class="no-entity">{i18n('There is nothing here')}</p>
  {/if}
  <MissingEntityButton {section} />
</div>

<style lang="scss">
  @import "general/scss/utils";
  .sub-entities-browser-section{
    background-color: $off-white;
    padding: 0.5em;
    margin-block-end: 0.5em;
    @include display-flex(column);
    &.disabled{
      opacity: 0.5;
    }
  }
  .section-without-entity{
    @include display-flex(row, center);
  }
  .title-row{
    @include display-flex(row, center, space-between, wrap);
    margin: 0.3em 0.5em;
  }
  ul{
    flex: 1;
    max-block-size: 42em;
    overflow-y: auto;
    &.list{
      align-self: stretch;
    }
    &.grid{
      @include display-flex(row, center, flex-start, wrap);
    }
    :global(.entity-wrapper){
      inline-size: 100%;
    }
  }
  li{
    @include display-flex(row, inherit, space-between);
  }
  .isCompactDisplay{
    inline-size: 100%;
  }
  .no-entity{
    color: $grey;
    margin: auto;
  }
  /* Smaller screens */
  @media screen and (width < $smaller-screen){
    li{
      @include display-flex(column);
      :global(.actions-wrapper){
        margin-block: 1em 0;
      }
    }
  }
</style>
